import React, { useEffect, useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { buildings } from '../services/api';
import api from '../services/api';

const meetingsApi = {
  list:            (params) => api.get('/meetings', { params }),
  create:          (data)   => api.post('/meetings', data),
  get:             (id)     => api.get(`/meetings/${id}`),
  update:          (id, d)  => api.put(`/meetings/${id}`, d),
  delete:          (id)     => api.delete(`/meetings/${id}`),
  uploadAudio:     (id, fd) => api.post(`/meetings/${id}/upload-audio`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  transcribe:      (id)     => api.post(`/meetings/${id}/transcribe`),
  generateMinutes: (id, d)  => api.post(`/meetings/${id}/generate-minutes`, d),
  suggestAgenda:   (id)     => api.post(`/meetings/${id}/suggest-agenda`),
};

const TYPE_COLORS   = { AGM: 'bg-purple-100 text-purple-700', council: 'bg-blue-100 text-blue-700', special: 'bg-orange-100 text-orange-700', committee: 'bg-green-100 text-green-700' };
const STATUS_COLORS = { scheduled: 'bg-yellow-100 text-yellow-700', completed: 'bg-green-100 text-green-700', cancelled: 'bg-gray-100 text-gray-500' };

const AGENDA_PRIORITY_COLORS = { high: 'bg-red-100 text-red-700', medium: 'bg-orange-100 text-orange-700', low: 'bg-gray-100 text-gray-500' };
const AGENDA_TYPE_COLORS     = { urgent: 'bg-red-100 text-red-700', approval: 'bg-indigo-100 text-indigo-700', discussion: 'bg-blue-100 text-blue-700', update: 'bg-green-100 text-green-700', standard: 'bg-gray-100 text-gray-500' };

function AgendaItem({ item, index }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="px-4 py-3 hover:bg-gray-50 transition-colors">
      <div className="flex items-start gap-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <span className="w-5 h-5 bg-gray-100 rounded-full text-xs flex items-center justify-center shrink-0 font-medium text-gray-500 mt-0.5">
          {index + 1}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-1">
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${AGENDA_PRIORITY_COLORS[item.priority] || ''}`}>{item.priority}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${AGENDA_TYPE_COLORS[item.type] || ''}`}>{item.type}</span>
            <span className="text-xs text-gray-400">{item.time_estimate}</span>
            {item.is_controversial && <span className="text-xs text-orange-600 font-medium">⚠️ May be contested</span>}
          </div>
          <p className="text-sm font-semibold text-gray-900">{item.title}</p>
          {item.context && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{item.context}</p>}
        </div>
        <svg className={`w-4 h-4 text-gray-400 shrink-0 mt-0.5 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      {expanded && item.talking_points?.length > 0 && (
        <div className="ml-8 mt-2 space-y-1 pb-1">
          <p className="text-xs font-medium text-gray-500">Talking points:</p>
          <ul className="space-y-0.5">
            {item.talking_points.map((tp, ti) => (
              <li key={ti} className="text-xs text-gray-600 flex gap-1.5">
                <span className="text-violet-400 shrink-0">•</span>{tp}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function CreateModal({ buildingList, onClose, onSave }) {
  const [form, setForm] = useState({ title: '', meeting_type: 'council', building_id: '', scheduled_date: '', location: '', agenda: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await meetingsApi.create(form);
      toast.success('Meeting created'); onSave();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-semibold text-gray-900">New Meeting</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div><label className="label">Title *</label><input className="input" value={form.title} onChange={set('title')} required placeholder="January Council Meeting" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Type</label>
              <select className="input" value={form.meeting_type} onChange={set('meeting_type')}>
                {['AGM','council','special','committee'].map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
              </select>
            </div>
            <div><label className="label">Building</label>
              <select className="input" value={form.building_id} onChange={set('building_id')}>
                <option value="">None</option>
                {buildingList.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div><label className="label">Date & Time</label><input type="datetime-local" className="input" value={form.scheduled_date} onChange={set('scheduled_date')} /></div>
            <div><label className="label">Location</label><input className="input" value={form.location} onChange={set('location')} placeholder="Building lobby" /></div>
          </div>
          <div><label className="label">Agenda</label><textarea className="input resize-none" rows={4} value={form.agenda} onChange={set('agenda')} placeholder="1. Call to order&#10;2. Approval of previous minutes&#10;3. Financial report..." /></div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50">{saving ? 'Saving…' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MeetingDetail({ meetingId, onClose, onUpdated }) {
  const [meeting, setMeeting]                 = useState(null);
  const [minutes, setMinutes]                 = useState(null);
  const [pastedTranscript, setPastedTranscript] = useState('');
  const [loading, setLoading]                 = useState('');
  const [agenda, setAgenda]                   = useState([]);
  const [suggestingAgenda, setSuggestingAgenda] = useState(false);
  const audioRef = useRef();

  const load = async () => {
    const res = await meetingsApi.get(meetingId);
    setMeeting(res.data);
    setAgenda(res.data.ai_agenda || []);
    if (res.data.minutes) {
      try { setMinutes(JSON.parse(res.data.minutes)); } catch { setMinutes(null); }
    }
  };

  useEffect(() => { load(); }, [meetingId]);

  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('audio', file);
    setLoading('Uploading audio…');
    try {
      await meetingsApi.uploadAudio(meetingId, fd);
      toast.success('Audio uploaded');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Upload failed'); }
    finally { setLoading(''); }
  };

  const handleTranscribe = async () => {
    setLoading('Transcribing with Groq Whisper… this may take a minute');
    try {
      await meetingsApi.transcribe(meetingId);
      toast.success('Transcription complete');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Transcription failed'); }
    finally { setLoading(''); }
  };

  const handleGenerateMinutes = async () => {
    setLoading('Generating minutes with AI…');
    try {
      const res = await meetingsApi.generateMinutes(meetingId, { transcript: pastedTranscript || undefined });
      setMinutes(res.data.minutes);
      toast.success('Minutes generated');
      onUpdated();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to generate minutes'); }
    finally { setLoading(''); }
  };

  const handleSuggestAgenda = async () => {
    setSuggestingAgenda(true);
    try {
      const res = await meetingsApi.suggestAgenda(meetingId);
      setAgenda(res.data.agenda || []);
      toast.success(`AI generated ${res.data.agenda?.length || 0} agenda items`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to generate agenda');
    } finally {
      setSuggestingAgenda(false);
    }
  };

  const handleAcceptAgenda = async () => {
    const agendaText = agenda
      .map((item, i) => `${i + 1}. ${item.title} (${item.time_estimate})\n   ${item.context}`)
      .join('\n\n');
    try {
      await meetingsApi.update(meetingId, { agenda: agendaText });
      toast.success('Agenda saved to meeting');
      load();
    } catch { toast.error('Failed to save agenda'); }
  };

  if (!meeting) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 overflow-y-auto">
      <div className="flex min-h-full items-start justify-center p-4 sm:items-center">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl flex flex-col" style={{ maxHeight: 'calc(100vh - 2rem)' }}>
        {/* Sticky header */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <div>
            <h2 className="font-semibold text-gray-900">{meeting.title}</h2>
            <p className="text-sm text-gray-400">{meeting.building_name} · {meeting.scheduled_date ? new Date(meeting.scheduled_date).toLocaleString() : 'No date set'}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg shrink-0">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Scrollable body */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {loading && (
            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl text-blue-700 text-sm">
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
              {loading}
            </div>
          )}

          {/* ── AI Agenda Suggestion ── */}
          <div className="border border-violet-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-violet-50 to-indigo-50 border-b border-violet-100">
              <div className="flex items-center gap-2">
                <span className="text-base">🤖</span>
                <h3 className="font-medium text-gray-900 text-sm">AI Suggested Agenda</h3>
                {agenda.length > 0 && (
                  <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full font-medium">
                    {agenda.length} items
                  </span>
                )}
              </div>
              <button
                onClick={handleSuggestAgenda}
                disabled={suggestingAgenda || !!loading}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium rounded-lg disabled:opacity-50 transition-colors"
              >
                {suggestingAgenda ? (
                  <>
                    <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
                    Generating…
                  </>
                ) : (
                  <>✨ {agenda.length > 0 ? 'Regenerate' : 'Suggest Agenda'}</>
                )}
              </button>
            </div>

            {agenda.length > 0 ? (
              <>
                <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
                  {agenda.map((item, i) => (
                    <AgendaItem key={i} item={item} index={i} />
                  ))}
                </div>
                <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-between">
                  <p className="text-xs text-gray-400">Based on your building's work orders and history</p>
                  <button
                    onClick={handleAcceptAgenda}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
                  >
                    📋 Use as Meeting Agenda
                  </button>
                </div>
              </>
            ) : (
              <div className="px-4 py-6 text-center">
                <p className="text-sm text-gray-400">Click "Suggest Agenda" to generate an AI-powered agenda</p>
                <p className="text-xs text-gray-300 mt-1">Based on your building's open work orders, maintenance requests, and past meeting decisions</p>
              </div>
            )}
          </div>

          {/* Step 0: Existing manual agenda */}
          {meeting.agenda && (
            <div>
              <h3 className="font-medium text-gray-900 mb-2 text-sm flex items-center gap-2">
                <span className="w-5 h-5 bg-gray-100 text-gray-500 rounded-full text-xs flex items-center justify-center font-bold">📋</span>
                Current Agenda
              </h3>
              <pre className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap font-sans">{meeting.agenda}</pre>
            </div>
          )}

          {/* Step 1: Audio upload */}
          <div className="border rounded-xl p-4">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full text-xs flex items-center justify-center font-bold">1</span>
              Upload Meeting Recording
            </h3>
            {meeting.has_audio ? (
              <div className="flex items-center gap-3">
                <span className="text-sm text-green-600 font-medium">✓ Audio file uploaded</span>
                <button onClick={handleTranscribe} disabled={!!loading} className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {meeting.has_transcript ? 'Re-transcribe' : 'Transcribe with AI'}
                </button>
              </div>
            ) : (
              <>
                <input ref={audioRef} type="file" accept=".mp3,.mp4,.wav,.m4a,.webm,.ogg,.flac" className="hidden" onChange={handleAudioUpload} />
                <button onClick={() => audioRef.current.click()} className="px-4 py-2 border-2 border-dashed border-gray-300 hover:border-blue-400 rounded-lg text-sm text-gray-500 hover:text-blue-600 w-full text-center">
                  Click to upload audio (mp3, wav, m4a, webm)
                </button>
              </>
            )}
          </div>

          {/* Step 2: Transcript */}
          <div className="border rounded-xl p-4">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full text-xs flex items-center justify-center font-bold">2</span>
              Transcript
              {meeting.has_transcript && <span className="text-xs text-green-600 font-medium ml-2">✓ Ready</span>}
            </h3>
            {meeting.has_transcript ? (
              <pre className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto whitespace-pre-wrap font-sans">{meeting.transcript}</pre>
            ) : (
              <div>
                <p className="text-sm text-gray-400 mb-2">No audio? Paste transcript directly:</p>
                <textarea className="input resize-none text-sm" rows={5} value={pastedTranscript} onChange={e => setPastedTranscript(e.target.value)} placeholder="Paste meeting transcript here…" />
              </div>
            )}
          </div>

          {/* Step 3: Generate minutes */}
          <div className="border rounded-xl p-4">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full text-xs flex items-center justify-center font-bold">3</span>
              AI Meeting Minutes
              {meeting.has_minutes && <span className="text-xs text-green-600 font-medium ml-2">✓ Generated</span>}
            </h3>
            <button onClick={handleGenerateMinutes} disabled={!!loading || (!meeting.has_transcript && !pastedTranscript)}
              className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
              Generate Minutes with AI
            </button>
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-800 mt-3">
              <svg className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span><strong>Review AI-generated minutes</strong> before distributing to owners or council members.</span>
            </div>

            {minutes && (
              <div className="mt-4 space-y-4">
                <div className="bg-green-50 rounded-lg p-3 text-sm text-green-800">{minutes.summary}</div>
                {minutes.sections?.map((s, i) => (
                  <div key={i}>
                    <h4 className="font-medium text-gray-800 text-sm">{s.heading}</h4>
                    <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{s.content}</p>
                  </div>
                ))}
                {minutes.decisions?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-800 text-sm mb-1">Decisions Made</h4>
                    <ul className="space-y-1">{minutes.decisions.map((d, i) => <li key={i} className="text-sm text-gray-600 flex gap-2"><span className="text-green-500">✓</span>{d}</li>)}</ul>
                  </div>
                )}
                {minutes.action_items?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-800 text-sm mb-2">Action Items</h4>
                    <div className="space-y-2">
                      {minutes.action_items.map((a, i) => (
                        <div key={i} className="bg-yellow-50 rounded-lg px-3 py-2 text-sm">
                          <span className="font-medium">{a.task}</span>
                          {a.assigned_to && <span className="text-gray-500 ml-2">→ {a.assigned_to}</span>}
                          {a.due_date && <span className="text-gray-400 ml-2">by {a.due_date}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export default function Meetings() {
  const [list, setList] = useState([]);
  const [buildingList, setBuildingList] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = () => {
    meetingsApi.list().then(r => setList(r.data)).catch(() => toast.error('Failed to load meetings'));
  };

  useEffect(() => {
    load();
    buildings.list().then(r => setBuildingList(r.data)).catch(() => {});
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this meeting?')) return;
    await meetingsApi.delete(id);
    toast.success('Deleted'); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Meetings</h1>
          <p className="text-gray-500 text-sm mt-1">AI-powered agendas + minutes from audio recordings</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          New Meeting
        </button>
      </div>

      {list.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-16 text-center text-gray-400">
          <p className="text-lg">No meetings yet.</p>
          <button onClick={() => setShowCreate(true)} className="mt-2 text-sm text-blue-600 hover:underline">Create your first meeting →</button>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map(m => (
            <div key={m.id} className="bg-white rounded-xl border border-gray-200 px-5 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_COLORS[m.meeting_type] || 'bg-gray-100'}`}>{m.meeting_type}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[m.status] || 'bg-gray-100'}`}>{m.status}</span>
                    {m.has_minutes && <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full">✓ Minutes</span>}
                    {m.ai_agenda?.length > 0 && <span className="text-xs bg-violet-50 text-violet-700 px-2 py-0.5 rounded-full">🤖 AI Agenda</span>}
                    {m.has_audio && !m.has_minutes && <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">Audio ready</span>}
                  </div>
                  <h3 className="font-semibold text-gray-900">{m.title}</h3>
                  <div className="flex gap-3 mt-1 text-xs text-gray-400 flex-wrap">
                    {m.building_name && <span>{m.building_name}</span>}
                    {m.location && <span>· {m.location}</span>}
                    {m.scheduled_date && <span>· {new Date(m.scheduled_date).toLocaleDateString()}</span>}
                  </div>
                  {m.action_items?.length > 0 && (
                    <p className="text-xs text-orange-600 mt-1">{m.action_items.length} action item{m.action_items.length !== 1 ? 's' : ''}</p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => setDetailId(m.id)} className="text-xs text-blue-600 hover:underline">Open</button>
                  <button onClick={() => handleDelete(m.id)} className="text-xs text-red-500 hover:underline">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && <CreateModal buildingList={buildingList} onClose={() => setShowCreate(false)} onSave={() => { setShowCreate(false); load(); }} />}
      {detailId && <MeetingDetail meetingId={detailId} onClose={() => setDetailId(null)} onUpdated={load} />}

      <style>{`.label{display:block;font-size:.75rem;font-weight:500;color:#374151;margin-bottom:.25rem}.input{width:100%;border:1px solid #d1d5db;border-radius:.5rem;padding:.5rem .75rem;font-size:.875rem;outline:none}.input:focus{border-color:#3b82f6;box-shadow:0 0 0 3px rgba(59,130,246,.1)}`}</style>
    </div>
  );
}
